import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './motion.css';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { TaskDetailBody } from '../../pages/TwoDays/TwoDaysTaskDetail';
import { ReactComponent as BoardIcon } from '@repo/common/assets/icons/16px/icon_16px_dashboard_outline.svg';
import { ReactComponent as ListIcon } from '@repo/common/assets/icons/16px/icon_16px_clipboard_outline.svg';
import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_visible_outline.svg';
import { ReactComponent as DoubleArrowRightIcon } from '@repo/common/assets/icons/16px/icon_16px_doublearrow_right.svg';
import { ReactComponent as FullscreenIcon } from '@repo/common/assets/icons/16px/icon_16px_fullscreen.svg';
import {
  JOB_LABEL,
  STATE_LABEL,
  STATE_TONE,
  assigneesOf,
  byJob,
  cn,
  isAssigneeOf,
  isLeader,
  visibleTasks,
} from './model/derive';
import {
  ALL_VIEWS,
  COLUMNS,
  MY_LABELS,
  MY_VIEWS,
  isFolded,
  isMineOrToMe,
  loadFolds,
  loadLayout,
  loadMyView,
  peopleFor,
  personHeading,
  saveFolds,
  saveLayout,
  saveMyView,
  sortTasks,
  toReviewCount,
} from './model/taskViews';
import { formatClock, formatDay } from './model/time';
import { dueInfo, useWorkboardCtx } from './model/useWorkboard';
import { ColumnBoard, SwimlaneView } from './BoardViews';
import ListView from './ListView';
import TimelineView from './TimelineView';
import TeamPanel from './TeamPanel';
import AddTaskModal from './AddTaskModal';
import SidePanel from './SidePanel';
import TaskCard from './TaskCard';
import { StatusChip } from './primitives';
import PersonChip from './PersonChip';
import TdButton from './Button';
import useMedia, { MOBILE } from './useMedia';
import { panelTo } from './panelLink';

const iconClass = 'h-4 w-4';
const SIDE_TEAM_MIN = 800;

/**
 * 태스크 패널 — 보드 위 오른쪽 600px (스크림 없음). 열린 채로 다른 카드·행·타임라인 막대를 누르면 그 태스크로 바뀌고,
 * 바깥 빈 곳 · » · Esc 로 닫는다. Open full page 로 상세 페이지에 간다.
 */
const TaskPanel = ({ open, taskId, onClose }) => {
  const ctx = useWorkboardCtx();
  const { pathname } = useLocation();
  return (
    <SidePanel open={open && !!taskId} onClose={onClose} label='Task panel' modeless>
      <div className='flex shrink-0 items-center justify-between gap-2 px-2 pb-4 pt-5 sm:px-4'>
        <button
          type='button'
          aria-label='Close panel'
          title='Close panel'
          onClick={onClose}
          className='flex h-8 w-8 items-center justify-center rounded-lg text-icon-secondary hover:bg-green-deep03/[0.08] hover:text-icon-primary'
        >
          <DoubleArrowRightIcon className={iconClass} />
        </button>
        <TdButton
          as={Link}
          variant='ghost'
          size='s'
          icon={<FullscreenIcon className={iconClass} />}
          to={`${ctx.base}/tasks/${taskId}`}
          state={pathname.endsWith('/tasks/mine') ? { from: 'mine' } : undefined}
          className='gap-1.5 px-2'
        >
          Open full page
        </TdButton>
      </div>
      {/* 다른 태스크로 바뀌면 맨 위부터 */}
      <div key={taskId} className='min-h-0 flex-1 overflow-y-auto px-5 pb-8 sm:px-7'>
        {taskId && <TaskDetailBody taskId={taskId} inPanel />}
      </div>
    </SidePanel>
  );
};

/**
 * 보기 탭 한 줄 — underline 탭. 활성 탭 밑줄이 탭 사이를 미끄러지듯 옮겨 간다.
 * To review 개수 배지: 1건 이상 = 빨강(failed03) · 흰 글자 (1022 2차 공통 배지), 0건 = 연한 초록 · text-secondary.
 */
const ViewTabs = ({ views, view, onChange, badges = {}, right }) => {
  const box = useRef(null);
  const [bar, setBar] = useState(null);
  useLayoutEffect(() => {
    const place = () => {
      const el = box.current?.querySelector('[aria-selected="true"]');
      if (el) setBar({ left: el.offsetLeft, width: el.offsetWidth });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [view, views]);
  return (
    <div className='flex items-center gap-4 border-b border-green-deep03/[0.12]'>
      <div
        ref={box}
        role='tablist'
        aria-label='View'
        className='relative flex min-w-0 flex-1 gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
      >
        {views.map(({ key, label }) => {
          const selected = key === view;
          const badge = badges[key];
          return (
            <button
              key={key}
              type='button'
              role='tab'
              aria-selected={selected}
              onClick={() => onChange(key)}
              className={cn(
                'flex h-11 shrink-0 items-center gap-1.5 px-3 text-[14px] transition-colors duration-200',
                selected
                  ? 'font-medium text-text-primary'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {label}
              {badge !== undefined && (
                <span
                  className={cn(
                    'flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[11px] font-medium leading-none tabular-nums transition-colors duration-200',
                    badge > 0
                      ? 'bg-functional-failed03 text-grayscale-gray00'
                      : 'bg-green-deep03/[0.08] text-text-secondary',
                  )}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
        {bar && (
          <span
            aria-hidden
            style={{ left: bar.left, width: bar.width }}
            className='absolute bottom-0 h-[2px] rounded-full bg-green-deep02 transition-[left,width] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none'
          />
        )}
      </div>
      {right}
    </div>
  );
};

/**
 * Display — 탭 줄 오른쪽 끝 텍스트 버튼. 누르면 Layout(Board / List) 팝오버.
 * 사용자별로 기억한다. Timeline 에서는 쓰지 않는다. 바깥 · Escape · 고르면 닫힌다.
 */
const DisplayMenu = ({ layout, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  return (
    <span ref={ref} data-pick='display' className='relative shrink-0'>
      <button
        type='button'
        aria-haspopup='dialog'
        aria-expanded={open}
        disabled={disabled}
        title={disabled ? 'Layout doesn’t apply to Timeline' : undefined}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'h-11 text-[14px] font-medium transition-colors disabled:cursor-default disabled:text-text-disable',
          open ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary',
        )}
      >
        Display
      </button>
      {open && (
        <div
          role='dialog'
          aria-label='Display'
          className='td-pop absolute right-0 top-full z-40 mt-1 flex w-[240px] origin-top-right flex-col gap-2 rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-3 shadow-popup-01'
        >
          <span className='text-[13px] font-medium text-text-secondary'>Layout</span>
          <div className='grid grid-cols-2 gap-2'>
            {[
              ['board', 'Board', BoardIcon],
              ['list', 'List', ListIcon],
            ].map(([key, label, Icon]) => {
              const on = layout === key;
              return (
                <button
                  key={key}
                  type='button'
                  aria-pressed={on}
                  onClick={() => {
                    onChange(key);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex h-16 flex-col items-center justify-center gap-1.5 rounded-xl text-[13px] transition-[background-color,box-shadow] duration-200',
                    on
                      ? 'bg-surface-green10 font-medium text-text-primary shadow-[inset_0_0_0_1.6px_#1dc37b]'
                      : 'text-text-secondary shadow-[inset_0_0_0_1px_rgba(0,158,79,0.12)] hover:bg-grayscale-gray01',
                  )}
                >
                  <Icon
                    className={cn(iconClass, on ? 'text-green-deep03' : 'text-icon-secondary')}
                  />
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </span>
  );
};

/** To review 빈 상태 — 다음에 넘어올 태스크가 있으면 회색 한 줄 */
const ReviewEmpty = ({ next }) => {
  const ctx = useWorkboardCtx();
  return (
    <div className='td-rise flex flex-col items-center gap-2 rounded-xl border border-dashed border-green-deep03/[0.24] bg-grayscale-gray00 px-6 py-8 text-center'>
      <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-surface-green10 text-green-deep03'>
        <VisibleIcon className='h-5 w-5' />
      </span>
      <p className='text-[14px] font-medium text-text-primary'>Nothing to review right now.</p>
      {next && (
        <p className='text-[13px] text-text-tertiary'>
          Next for you: {next.title} from{' '}
          {assigneesOf(next)
            .map((id) => ctx.memberById.get(id)?.name)
            .filter(Boolean)
            .join(', ')}{' '}
          · due {formatClock(dueInfo(ctx, next).effective, ctx.displayTz)}
        </p>
      )}
    </div>
  );
};

/** 모바일 Timeline — 날짜별 리스트 (일자 섹션 + 시각 · 제목 · 상태 칩 · 담당자) */
const TimelineDayList = ({ tasks }) => {
  const ctx = useWorkboardCtx();
  const location = useLocation();
  const due = (t) => dueInfo(ctx, t).effective;
  const sorted = [...tasks].sort((a, b) => due(a) - due(b));
  const days = [];
  sorted.forEach((t) => {
    const day = formatDay(due(t), ctx.displayTz);
    const last = days[days.length - 1];
    if (last?.day === day) last.tasks.push(t);
    else days.push({ day, tasks: [t] });
  });
  return (
    <div className='flex flex-col gap-4'>
      {days.map((d) => (
        <section key={d.day} className='flex flex-col gap-2'>
          <h3 className='flex min-h-[28px] items-center gap-2.5'>
            <span className='flex h-6 items-center whitespace-nowrap rounded-full bg-surface-green10 px-2.5 text-[13px] font-medium leading-none text-green-deep04'>
              {d.day}
            </span>
            <span
              aria-hidden
              className='min-w-[24px] flex-1 border-t-[1.5px] border-dotted border-green-deep03/40'
            />
            <span className='text-[12px] font-normal text-text-secondary'>
              {d.tasks.length} task{d.tasks.length === 1 ? '' : 's'}
            </span>
          </h3>
          <ul className='overflow-hidden rounded-xl border border-green-deep03/10 bg-grayscale-gray00'>
            {d.tasks.map((t, i) => (
              <li
                key={t.id}
                className='td-rise border-t border-green-deep03/[0.08] first:border-t-0'
                style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
              >
                <Link
                  to={panelTo(location, t.id)}
                  className='flex items-start gap-3 px-4 py-3 hover:bg-grayscale-gray01'
                >
                  <span className='w-11 shrink-0 pt-0.5 text-[13px] tabular-nums text-text-secondary'>
                    {formatClock(due(t), ctx.displayTz)}
                  </span>
                  <span className='flex min-w-0 flex-1 flex-col gap-1.5'>
                    <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
                      {t.title}
                    </span>
                    <span className='flex flex-wrap items-center gap-2'>
                      <StatusChip
                        chip={{ label: STATE_LABEL[t.state], tone: STATE_TONE[t.state] }}
                      />
                      <PersonChip
                        members={assigneesOf(t).map((id) => ctx.memberById.get(id))}
                        dot={false}
                      />
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

/**
 * 태스크 작업 화면 (1022 수정 1차 · ② Workboard · Task).
 *   scope 'mine' — My tasks: Assigned to me(기본) · To review. 고른 탭은 사용자별로 기억한다
 *   scope 'all'  — All tasks: Timeline · By progress · By assignee. 사이드바로 들어오면 늘 Timeline, ?view= 가 있을 때만 그 탭
 * 탭 줄 오른쪽 끝 Display(Board / List). By assignee · Timeline 에서는 오른쪽 팀 패널을 숨기고 전체 폭을 쓴다.
 */
const TaskWorkspace = ({ scope }) => {
  const ctx = useWorkboardCtx();
  const me = ctx.data.viewerId;
  const [params, setParams] = useSearchParams();
  const mobile = useMedia(MOBILE);
  const views = scope === 'mine' ? MY_VIEWS : ALL_VIEWS;
  const [savedMine, setSavedMine] = useState(() => loadMyView(me));
  const requested = params.get('view');
  const view = views.some((v) => v.key === requested)
    ? requested
    : scope === 'mine'
      ? savedMine
      : 'timeline';

  const [layout, setLayoutState] = useState(() => loadLayout(me));
  const setLayout = (l) => {
    setLayoutState(l);
    saveLayout(me, l);
  };
  const [folds, setFolds] = useState(() => loadFolds(me));
  const fold = (col, folded) =>
    setFolds((cur) => {
      const next = { ...cur, [`${view}:${col.key}`]: folded };
      saveFolds(me, next);
      return next;
    });

  const rootRef = useRef(null);
  const [rootWidth, setRootWidth] = useState(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setRootWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 진행 중 배정은 리더만 — 팀원 화면에는 Add task 가 없다 (PM·Leader / 팀원 시안)
  const canAssign = isLeader(ctx.viewer) && !ctx.readOnly;
  const ended = ctx.data.project.status === 'ended';
  const [adding, setAdding] = useState(false);
  const [adjusting, setAdjusting] = useState(false); // 타임라인 조정 중에는 Add task 를 숨긴다
  const showTeam = scope === 'mine' || view === 'progress';
  // 취소된 태스크는 보이지 않는다
  const tasks = visibleTasks(ctx.data.tasks);

  // 태스크 패널 — 카드·행·막대를 누르면 ?task=<id> 로 오른쪽에 연다 (Task Flows 1a)
  const panelId = params.get('task');
  // 닫히는 동안에도 내용이 남아 있어야 밀려 나가는 게 보인다 — 마지막으로 연 태스크를 기억한다
  const [shownPanelId, setShownPanelId] = useState(panelId);
  useEffect(() => {
    if (panelId && ctx.taskById.get(panelId)) setShownPanelId(panelId);
  }, [panelId, ctx.taskById]);
  const closePanel = () => {
    const next = new URLSearchParams(params);
    next.delete('task');
    setParams(next, { replace: true });
  };

  const setView = (v) => {
    const next = new URLSearchParams(params);
    const home = scope === 'mine' ? 'assigned' : 'timeline';
    if (v === home) next.delete('view');
    else next.set('view', v);
    if (scope === 'mine') {
      setSavedMine(v);
      saveMyView(me, v);
    }
    setParams(next, { replace: true });
  };

  const dueMs = (t) => dueInfo(ctx, t).effective.getTime();
  const defs = COLUMNS[view] ?? [];
  const columns = defs.map((col) => ({
    col,
    tasks: sortTasks(
      tasks.filter((t) => col.match(t, me)),
      ctx,
      dueMs,
    ),
  }));
  const folded = (col) => isFolded(folds, view, col);

  // 카드 · 행에 넘길 것 — 사람(요청자 · 담당자), You, 상태 이름
  const cardProps = (t, { lane } = {}) =>
    scope === 'mine'
      ? { people: peopleFor(t, view, ctx), labels: MY_LABELS }
      : { people: lane ? [] : peopleFor(t, view, ctx), you: isMineOrToMe(t, me) };
  const renderCard = (t, opts) => <TaskCard task={t} {...cardProps(t, opts)} />;

  // To review 빈 상태 — 받는 사람 = 나 + assigned/in_progress 중 마감이 가장 이른 1건
  const nextForMe = sortTasks(
    tasks.filter((t) => t.receiverId === me && ['assigned', 'in_progress'].includes(t.state)),
    { data: { viewerId: me } },
    dueMs,
  )[0];
  const emptySlot = (col) => (col.key === 'to_review' ? <ReviewEmpty next={nextForMe} /> : null);

  // My tasks 에 내 태스크가 하나도 없으면 (담당자 · 받는 사람 어느 쪽도 아님)
  const noneMine = scope === 'mine' && !tasks.some((t) => isMineOrToMe(t, me));
  const noTasks = tasks.length === 0;

  // List — By assignee 는 팀원별 섹션, 나머지는 열 순서 그대로
  const lanes = [...ctx.memberById.values()]
    .filter((m) => tasks.some((t) => isAssigneeOf(t, m.id)))
    .sort((a, b) => Number(b.id === me) - Number(a.id === me) || byJob(a, b));
  const sections =
    view === 'assignee'
      ? lanes.map((m) => {
          const key = `lane:${m.id}`;
          return {
            key,
            label: `${m.name} · ${JOB_LABEL[m.job] ?? m.job}`,
            head: <PersonChip members={[m]} />,
            tasks: sortTasks(
              tasks.filter((t) => isAssigneeOf(t, m.id)),
              ctx,
              dueMs,
            ),
            folded: folds[`assignee:${key}`] ?? false,
            onToggle: () => fold({ key }, !(folds[`assignee:${key}`] ?? false)),
          };
        })
      : columns.map(({ col, tasks: list }) => ({
          key: col.key,
          label: col.label,
          tasks: list,
          folded: folded(col),
          onToggle: () => fold(col, !folded(col)),
        }));

  const body = () => {
    if (noTasks) {
      return (
        <p className='rounded-[14px] border border-grayscale-gray02 bg-grayscale-gray00 px-6 py-12 text-center text-[14px] text-text-secondary'>
          No tasks yet.
        </p>
      );
    }
    if (noneMine) {
      return (
        <p className='rounded-[14px] border border-grayscale-gray02 bg-grayscale-gray00 px-6 py-12 text-center text-[14px] text-text-secondary'>
          No tasks where you’re the assignee or receiver.
        </p>
      );
    }
    if (view === 'timeline') {
      return mobile ? (
        <TimelineDayList tasks={tasks} />
      ) : (
        <TimelineView tasks={tasks} showEveryone onAdjustingChange={setAdjusting} />
      );
    }
    if (layout === 'list') {
      return (
        <div className='flex flex-col gap-3'>
          {view === 'review' && columns[0]?.tasks.length === 0 && <ReviewEmpty next={nextForMe} />}
          <ListView
            sections={sections.filter(
              (s) => !(view === 'review' && s.key === 'to_review' && !s.tasks.length),
            )}
            personHeading={personHeading(view)}
            renderProps={(t) => cardProps(t)}
          />
        </div>
      );
    }
    if (view === 'assignee')
      return <SwimlaneView columns={defs} tasks={tasks} renderCard={renderCard} />;
    return (
      <ColumnBoard
        columns={columns}
        folded={folded}
        onFold={fold}
        renderCard={renderCard}
        emptySlot={emptySlot}
      />
    );
  };

  return (
    // 팀 패널은 오른쪽 248px 고정 (본문 폭 800px~), 그보다 좁으면(태블릿 · Inbox 를 열었을 때) 보드 아래로
    <div
      ref={rootRef}
      className={cn(
        'grid items-start gap-x-10 gap-y-8',
        showTeam &&
          (rootWidth == null
            ? 'tabletV:grid-cols-[minmax(0,1fr)_248px]'
            : rootWidth >= SIDE_TEAM_MIN && 'grid-cols-[minmax(0,1fr)_248px]'),
      )}
    >
      <div className='flex min-w-0 flex-col gap-5'>
        <div className='flex min-h-10 items-center justify-between gap-4'>
          <h1 className='m-0 flex items-center gap-3 text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
            {scope === 'mine' ? 'My tasks' : 'All tasks'}
            {/* 끝난 프로젝트 — 읽기 전용 */}
            {ended && <StatusChip chip={{ label: 'Completed', tone: 'success' }} />}
          </h1>
          {canAssign &&
            !adjusting &&
            (ctx.actionsLocked ? (
              <TdButton disabled title={ctx.lockTip} className='shrink-0'>
                Add task
              </TdButton>
            ) : (
              <TdButton onClick={() => setAdding(true)} className='shrink-0'>
                Add task
              </TdButton>
            ))}
        </div>

        <ViewTabs
          views={views}
          view={view}
          onChange={setView}
          badges={scope === 'mine' ? { review: toReviewCount(tasks, me) } : {}}
          right={
            <DisplayMenu layout={layout} onChange={setLayout} disabled={view === 'timeline'} />
          }
        />

        {/* 탭 · 레이아웃을 바꾸면 내용이 살짝 떠오르며 바뀐다 */}
        <div key={`${view}:${layout}`} className='td-fade min-w-0'>
          {body()}
        </div>
      </div>

      {adding && <AddTaskModal onClose={() => setAdding(false)} />}
      <TaskPanel
        open={!!panelId && !!ctx.taskById.get(panelId)}
        taskId={shownPanelId}
        onClose={closePanel}
      />

      {showTeam && (
        <aside className='flex min-w-0 flex-col gap-6'>
          <TeamPanel />
        </aside>
      )}
    </div>
  );
};

export default TaskWorkspace;
